<template>
  <div class="mandate-row">
    <div class="mandate-row__label">
      <div class="d-flex align-items-center gap-2">
        <MandateLinkComponent
          :mandateId="row.mandate_id"
          class="mandate-row__name text-truncate"
          >{{ row.name }}</MandateLinkComponent
        >
        <InfeasibilityDotComponent :periods="row.infeasible_periods" />
      </div>
      <div class="d-flex align-items-center gap-1 flex-wrap mt-1">
        <ForwardsStatusBadgeComponent
          v-if="row.listing_kind === 'pay_as_produced'"
          status="pay_as_produced"
        />
        <ForwardsStatusBadgeComponent
          v-if="row.is_escrow_remaining"
          status="escrow_remaining"
        />
        <ForwardsStatusBadgeComponent
          v-if="row.is_archived"
          status="archived"
        />
      </div>
      <div class="mandate-row__subtitle">{{ subtitle }}</div>
    </div>

    <template v-if="papBands.length">
      <InfoTooltip
        v-for="band in papBands"
        :key="band.key"
        :text="band.label"
        :showIcon="false"
        class="mandate-row__pap"
        :style="{ gridColumn: `${band.column} / span ${band.span}` }"
      >
        <span
          class="mandate-row__pap-text"
          role="img"
          :aria-label="band.label"
          >{{ band.label }}</span
        >
      </InfoTooltip>
    </template>
    <template v-else>
      <MandateTimelineCellComponent
        v-for="slot in slots"
        :key="slot.quarter"
        :cell="slot.cell"
        :span="slot.span"
        :quarter="slot.quarter"
        :mandateName="row.name"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import MandateLinkComponent from "@/forwards2/components/MandateLinkComponent.vue";
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import ForwardsStatusBadgeComponent from "@/forwards2/components/ForwardsStatusBadgeComponent.vue";
import InfeasibilityDotComponent from "@/forwards2/components/InfeasibilityDotComponent.vue";
import type { MandateRow } from "@/forwards2/forwardsTypes";
import {
  parseQuarterLabel,
  placeCells,
  quarterLabelOfDate,
  QUARTERS_PER_YEAR,
} from "@/forwards2/periodUtils";
import MandateTimelineCellComponent from "@/forwards2/mandates/MandateTimelineCellComponent.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * One mandate as a row of the timeline: an identity column plus one grid cell per visible quarter.
 *
 * A pay-as-produced mandate has no listed volumes at all — it sells its devices' whole output — so it
 * draws each contract window as a hatched span carrying that contract's escrowed and delivered
 * figures instead of per-quarter volume cells; the figures repeat as a tooltip, since a one-quarter
 * span truncates them.
 */
const props = defineProps<{
  row: MandateRow;
  /** The window's quarter labels, in order — the row's columns. */
  windowQuarters: string[];
}>();

const { t } = useI18n();

const slots = computed(() => placeCells(props.windowQuarters, props.row.cells));

const subtitle = computed(() =>
  t("forwards.mandates.row_subtitle", {
    companies: t("forwards.mandates.row_companies", props.row.member_count),
    devices: t("forwards.common.device_count", props.row.device_count),
    shape: shapeLabel.value,
  })
);

const shapeLabel = computed(() => {
  const kind = t(`forwards.mandates.listing_kind.${props.row.listing_kind}`);
  if (props.row.entry_width === null) return kind;
  return `${kind}, ${t(`forwards.mandates.entry_width.${props.row.entry_width}`)}`;
});

/**
 * One hatched band per contract that runs in a visible quarter, earliest first (Q-FIX5-01).
 *
 * A contract with no visible quarter is left out rather than clamped: a window that ended before
 * the columns — or starts after them — would otherwise be drawn as running across them
 * (RV-mandates-11). A renewal signed inside the quarter its predecessor ended in shares that column,
 * so each band starts after the previous one's last column and two bands never overlap.
 */
const papBands = computed(() => {
  const columns = props.windowQuarters;
  const bands: { key: string; column: number; span: number; label: string }[] =
    [];
  let firstFreeIndex = 0;

  for (const contract of props.row.pap_contracts) {
    const first = offsetInWindow(
      columns,
      quarterLabelOfDate(contract.window_start)
    );
    const last = offsetInWindow(
      columns,
      quarterLabelOfDate(contract.window_end)
    );
    if (first === null || last === null) continue;

    const startIndex = Math.max(firstFreeIndex, first);
    const endIndex = Math.min(columns.length - 1, last);
    if (endIndex < startIndex) continue;
    firstFreeIndex = endIndex + 1;

    bands.push({
      key: `${contract.window_start}/${contract.window_end}`,
      // The identity column is the grid's first, so a quarter sits at its offset plus two.
      column: startIndex + 2,
      span: endIndex - startIndex + 1,
      label: t("forwards.mandates.pap_window", {
        escrowed: formatNumberWithSpaces(contract.escrowed),
        delivered: formatNumberWithSpaces(contract.delivered),
      }),
    });
  }
  return bands;
});

/**
 * How many columns `quarter` sits after the first visible one — negative before the window, past
 * `columns.length - 1` after it, so the caller can tell the two directions apart before clamping.
 *
 * @returns the signed offset, or null when either label is not a quarter.
 */
function offsetInWindow(
  columns: string[],
  quarter: string | null
): number | null {
  if (quarter === null || columns.length === 0) return null;

  const start = parseQuarterLabel(columns[0]);
  const target = parseQuarterLabel(quarter);
  if (!start || !target) return null;
  return (
    (target.year - start.year) * QUARTERS_PER_YEAR +
    (target.quarter - start.quarter)
  );
}
</script>

<style scoped lang="scss">
@use "@/forwards2/forwards-colors" as colors;

.mandate-row {
  display: grid;
  grid-template-columns: 280px repeat(4, 1fr);
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0;
  border-top: 1px solid var(--phoenix-border-color-translucent);
}

.mandate-row__label {
  min-width: 0;
  padding-right: 0.75rem;
}

.mandate-row__name {
  font-weight: 600;
  color: var(--phoenix-gray-1000);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.mandate-row__subtitle {
  font-size: 0.6875rem;
  color: var(--phoenix-gray-600);
}

.mandate-row__pap {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1.25rem;
  margin: 0 0.125rem;
  border-radius: 3px;
  background: repeating-linear-gradient(
    45deg,
    rgba(colors.$forwards-exclusive, 0.22),
    rgba(colors.$forwards-exclusive, 0.22) 4px,
    transparent 4px,
    transparent 8px
  );
  border: 1px solid rgba(colors.$forwards-exclusive, 0.45);
}

.mandate-row__pap-text {
  font-size: 0.625rem;
  color: var(--phoenix-gray-700);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 0.25rem;
}
</style>
